import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, CalendarDays } from "lucide-react";
import { Reveal } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";
import { POSTS } from "../data/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <section className="pt-[72px] min-h-[60vh] flex items-center" data-testid="blog-not-found">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
          <h1 className="font-display font-bold text-3xl text-ink mb-4">That article doesn't exist.</h1>
          <Link to="/blog" className="text-indigo font-semibold inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to all tips
          </Link>
        </div>
      </section>
    );
  }

  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <article className="pt-[72px]" data-testid="blog-post">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16">
          <Reveal>
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-slate text-sm font-medium hover:text-indigo transition-colors duration-200 mb-10">
              <ArrowLeft className="w-4 h-4" /> All tips & guides
            </Link>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[11px] font-semibold text-indigo bg-indigo/8 border border-indigo/15 rounded-full px-3 py-1">{post.tag}</span>
              <span className="text-xs text-slate flex items-center gap-1.5"><CalendarDays className="w-3 h-3" /> {post.date}</span>
              <span className="text-xs text-slate flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.readTime}</span>
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.1] mb-10" data-testid="blog-post-title">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6" data-testid="blog-post-body">
              {post.body.map((para, i) => (
                <p key={i} className={`leading-relaxed ${i === 0 ? "text-ink text-lg font-medium" : "text-ink/70 text-base"}`}>
                  {para}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </article>

      <section className="px-5 sm:px-8 pb-8" data-testid="blog-more">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display font-semibold text-ink text-lg mb-5">Keep reading</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="group bg-white rounded-2xl border border-ink/5 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
              >
                <h3 className="font-display font-semibold text-ink text-sm leading-snug group-hover:text-indigo transition-colors duration-200">{p.title}</h3>
                <span className="mt-3 inline-flex items-center gap-1.5 text-indigo text-xs font-semibold">
                  Read <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTAPanel />
    </>
  );
}
